import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle, Trash2, DollarSign, Send, FileEdit, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import StudentFeeStructureModal from './StudentFeeStructureModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const FeesPanel = ({ selectedClass }) => {
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalStudent, setModalStudent] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const classParam = selectedClass && selectedClass !== 'all' ? `?class_level=${selectedClass}` : '';
      const [studentsRes, feesRes, structsRes] = await Promise.all([
        axios.get(`${API}/head-teacher/students${classParam}`),
        axios.get(`${API}/head-teacher/fees${classParam}`),
        axios.get(`${API}/head-teacher/fee-structures`).catch(() => ({ data: { structures: [] } })),
      ]);
      const sMap = Object.fromEntries(
        (structsRes.data?.structures || []).map(s => [s.student_id, s])
      );
      setStudents((studentsRes.data || []).map(s => ({ ...s, fee_structure: sMap[s.id] || null })));
      setFees(feesRes.data || []);
    } catch {
      toast.error('Failed to load fees');
    } finally { setLoading(false); }
  }, [selectedClass]);

  useEffect(() => { load(); }, [load]);

  // Roll up fees per student
  const today = new Date().toISOString().slice(0, 10);
  const feesByStudent = students.map(s => {
    const rec = fees.filter(f => f.student_id === s.id);
    const paid = rec.filter(f => f.status === 'paid').reduce((sum, f) => sum + (f.amount || 0), 0);
    const pending = rec.filter(f => f.status === 'pending').reduce((sum, f) => sum + (f.amount || 0), 0);
    const overdue = rec.filter(f => f.status === 'pending' && (f.due_date || '9999') < today).reduce((sum, f) => sum + (f.amount || 0), 0);
    const annualTotal = paid + pending;
    const nextDue = rec.filter(f => f.status === 'pending').sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))[0];
    return { ...s, fee_records: rec, paid, pending, overdue, annualTotal, nextDue };
  });

  const markPaid = async (feeId) => {
    try { await axios.post(`${API}/head-teacher/fees/${feeId}/mark-paid`); load(); }
    catch { toast.error('Failed'); }
  };
  const remove = async (feeId) => {
    if (!window.confirm('Delete this fee record?')) return;
    try { await axios.delete(`${API}/head-teacher/fees/${feeId}`); load(); }
    catch { toast.error('Failed'); }
  };
  const sendReminder = async (feeId) => {
    setSendingId(feeId);
    try {
      const res = await axios.post(`${API}/head-teacher/fees/${feeId}/send-reminder`);
      toast.success(res.data?.dry_run ? 'Logged (dry-run — MSG91 not configured)' : 'SMS sent to parent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSendingId(null); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="fees-panel">
      {/* School-level summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Annual Fee', value: feesByStudent.reduce((s, x) => s + x.annualTotal, 0), color: 'bg-slate-700', sub: `${feesByStudent.length} students` },
          { label: 'Collected', value: feesByStudent.reduce((s, x) => s + x.paid, 0), color: 'bg-emerald-600' },
          { label: 'Pending', value: feesByStudent.reduce((s, x) => s + x.pending, 0), color: 'bg-amber-500' },
          { label: 'Overdue', value: feesByStudent.reduce((s, x) => s + x.overdue, 0), color: 'bg-red-600' },
        ].map((c, i) => (
          <div key={i} className={`${c.color} text-white rounded-xl p-4`}>
            <p className="text-xs uppercase opacity-80 tracking-wide">{c.label}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">₹{fmt(c.value)}</p>
            {c.sub && <p className="text-xs opacity-80 mt-1">{c.sub}</p>}
          </div>
        ))}
      </div>

      {/* Students with their fee structure */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
          <h3 className="font-semibold flex items-center gap-2"><DollarSign size={18} /> Per-Student Fees</h3>
          <p className="text-xs text-orange-50 mt-0.5">Set annual fee per student → auto-splits into terms → tracks paid / pending / overdue</p>
        </div>

        {feesByStudent.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">No students in this class yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Student</th>
                  <th className="text-center px-4 py-2.5">Class</th>
                  <th className="text-right px-4 py-2.5">Annual Fee</th>
                  <th className="text-right px-4 py-2.5">Discount</th>
                  <th className="text-right px-4 py-2.5">Paid</th>
                  <th className="text-right px-4 py-2.5">Pending</th>
                  <th className="text-left px-4 py-2.5 min-w-[180px]">Next Due</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {feesByStudent.map((s) => {
                  const hasStructure = s.fee_records.length > 0;
                  const status = !hasStructure ? 'unset' : s.overdue > 0 ? 'overdue' : s.pending > 0 ? 'pending' : 'paid';
                  return (
                    <tr key={s.id} className="hover:bg-orange-50/30" data-testid={`fee-student-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{s.name}</div>
                        <div className="text-xs text-gray-400">Roll {s.roll_number || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="text-xs">Class {s.class_level}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">
                        {hasStructure ? `₹${fmt(s.annualTotal)}` : <span className="text-gray-300 italic font-normal">not set</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {s.fee_structure && s.fee_structure.discount > 0 ? (
                          <span className="text-emerald-700 font-medium" title={s.fee_structure.discount_reason || ''}>
                            −₹{fmt(s.fee_structure.discount)}
                            {s.fee_structure.discount_reason && (
                              <p className="text-[10px] text-gray-500 font-normal truncate max-w-[120px]">{s.fee_structure.discount_reason}</p>
                            )}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700">₹{fmt(s.paid)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {s.overdue > 0 ? (
                          <span className="text-red-600 font-semibold">₹{fmt(s.pending)} <span className="text-[10px] font-normal">(₹{fmt(s.overdue)} overdue)</span></span>
                        ) : s.pending > 0 ? (
                          <span className="text-amber-700">₹{fmt(s.pending)}</span>
                        ) : hasStructure ? <span className="text-emerald-600 text-xs">All paid ✓</span> : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {s.nextDue ? (
                          <div>
                            <p className="font-medium text-gray-700">{s.nextDue.term_name}</p>
                            <p className="text-gray-500">₹{fmt(s.nextDue.amount)} · due {s.nextDue.due_date}</p>
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setModalStudent(s)} className="h-7 text-xs text-orange-700 hover:bg-orange-100" data-testid={`set-fee-${s.id}`}>
                          <FileEdit size={12} className="mr-1" /> {hasStructure ? 'Edit' : 'Set Fee'}
                        </Button>
                        {s.nextDue && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => markPaid(s.nextDue.id)} className="h-7 text-xs text-emerald-700 hover:bg-emerald-100" data-testid={`paid-${s.nextDue.id}`}>
                              <CheckCircle size={12} className="mr-1" /> Paid
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => sendReminder(s.nextDue.id)} disabled={sendingId === s.nextDue.id} className="h-7 text-xs text-indigo-700 hover:bg-indigo-100" data-testid={`send-sms-${s.nextDue.id}`} title="Send SMS reminder to parent">
                              {sendingId === s.nextDue.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            </Button>
                          </>
                        )}
                        {hasStructure && (
                          <Button size="sm" variant="ghost" onClick={() => s.fee_records.forEach(f => f.status === 'pending' && remove(f.id))} className="h-7 text-xs text-red-500 hover:bg-red-50" title="Clear all pending instalments">
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalStudent && (
        <StudentFeeStructureModal
          student={modalStudent}
          onClose={() => setModalStudent(null)}
          onSaved={() => { setModalStudent(null); load(); }}
        />
      )}
    </div>
  );
};

export default FeesPanel;
